import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../api/api";
import { migrateLegacyRoster, setSessionToken } from "../../lib/sessionToken";

/**
 * Dedicated platform admin login form for /platform-admin/login.
 * @param {{onSetAdmin: (admin: Object) => void}} props
 * @returns {JSX.Element}
 */
function AdminLogin({ onSetAdmin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");

  /**
   * Handle controlled input changes.
   * @param {React.ChangeEvent<HTMLInputElement>} e
   * @returns {void}
   */
  function handleChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  /**
   * Authenticate admin and route into isolated admin area.
   * @param {React.FormEvent<HTMLFormElement>} e
   * @returns {Promise<void>}
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const result = await loginUser(formData.username, formData.password);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (result?.data?.type !== "admin") {
      setError("This portal is for platform administrators only.");
      return;
    }
    migrateLegacyRoster();
    setSessionToken(result.token || "");
    onSetAdmin(result.data);
    navigate("/platform-admin/pending");
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-ui-background px-4">
      <div className="w-full max-w-md rounded-xl border border-ui-border bg-ui-card p-8 shadow-profile-md">
        <h2 className="mb-2 text-center text-3xl font-bold text-accent">Admin login</h2>
        <p className="mb-6 text-center text-sm text-text-secondary">
          Platform administration portal
        </p>
        {error ? (
          <p className="mb-4 rounded-md border border-ui-border bg-ui-muted px-3 py-2 text-sm text-text-secondary">
            {error}
          </p>
        ) : null}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-text-secondary">
              Username
            </label>
            <input
              type="text"
              name="username"
              id="username"
              required
              value={formData.username}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-ui-border bg-ui-background px-4 py-2 text-text-primary shadow-sm focus:border-accent focus:ring-accent"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-secondary">
              Password
            </label>
            <input
              type="password"
              name="password"
              id="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-ui-border bg-ui-background px-4 py-2 text-text-primary shadow-sm focus:border-accent focus:ring-accent"
            />
          </div>
          <button
            type="submit"
            className="w-full rounded-md border border-accent bg-accent py-2 font-semibold text-text-on-accent transition hover:bg-accent/90"
          >
            Sign in
          </button>
        </form>
      </div>
    </main>
  );
}

export default AdminLogin;
